import { visit } from 'unist-util-visit';
import type { Root, Element, Text } from 'hast';

/**
 * Rehype transform: rewrite ```query fenced code blocks into
 * `<div class="mb-query" data-query-source>…</div>` carrying the query text.
 *
 * Same shape as the mermaid transform, and for the same reason: the block needs
 * the *library* to render, which only the app has. The renderer fills it in
 * place (`apps/web/src/lib/query`), and anything that renders Markdown without
 * a library — a shared document, the server — simply shows nothing rather than
 * a broken query.
 *
 * After sanitize, so the emitted element is trusted structural output; the query
 * itself stays text and is never interpreted as markup.
 */
export function rehypeQuerySource() {
  return (tree: Root): void => {
    visit(tree, 'element', (node: Element, index, parent) => {
      if (node.tagName !== 'pre' || !parent || index === undefined) return;
      const code = node.children.find(
        (c): c is Element => c.type === 'element' && c.tagName === 'code',
      );
      if (!code) return;

      const classes = code.properties?.className;
      const classList = Array.isArray(classes) ? classes.map(String) : [];
      if (!classList.includes('language-query')) return;

      const source = code.children
        .filter((c): c is Text => c.type === 'text')
        .map((c) => c.value)
        .join('');

      (parent.children as Element[])[index] = {
        type: 'element',
        tagName: 'div',
        properties: { className: ['mb-query'], 'data-query-source': '' },
        children: [{ type: 'text', value: source }],
      };
    });
  };
}
